import { useToast } from "@expert/common-ui";
import { CopyIcon } from "@expert/common-ui/assets";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { getLogger } from "@expert/logging";
import { SoundNotificationsSwitch } from "@expert/proactive-notifications";
import { Button, Menu, Space, Stack, Text } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { useMemo, useState } from "react";
import { useAnalytics } from "@/analytics";
import { CaretDown, CaretUp, Exit, UserInitial } from "@/common-ui";
import { getSelectedSessionId, useAgentActivity, useAgentSdk, useAgentStore, useTypedMostRecentTask } from "@/sdk";
import { formatAgentName } from "@/shared-utils";
import { env } from "./environment";
import classes from "./Profile.module.css";
import { ThemeControls } from "./ThemeControls";

const logger = getLogger({ module: "Profile" });

interface ProfileProps {
    isProdMode: boolean;
    commitHash: string;
}

export function Profile({ commitHash }: ProfileProps) {
    const {
        features: { isProactiveEnabled, isLightModeEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const agentName = useAgentStore((state) => formatAgentName(state.agentName));
    const { activity } = useAgentStore();
    const { dispatcher } = useAnalytics();
    const clipboard = useClipboard({ timeout: 500 });

    const sessionId = getSelectedSessionId();
    const WorkerId = useAgentStore.getState().agentId;
    const Username = useAgentStore.getState().agentName;
    const { voiceTask, chatTask, task } = useTypedMostRecentTask();
    const toast = useToast();
    const agentSdk = useAgentSdk();
    const { setNewAgentActivity } = useAgentActivity();

    const [menuOpen, setMenuOpen] = useState(false);
    const loggerWithContext = useMemo(
        () =>
            logger.child({
                sessionId,
                callSid: voiceTask?.agentCallId,
                chatId: chatTask?.chatId,
            }),
        [sessionId, voiceTask?.agentCallId, chatTask?.chatId],
    );

    const copyToDebug = () => {
        clipboard.copy(
            JSON.stringify({
                SessionId: sessionId || "undefined",
                WorkerId: WorkerId || "undefined",
                UserName: Username || "undefined",
                TaskSid: task?.id ?? "undefined",
                ...(voiceTask
                    ? {
                          CallSid: voiceTask.agentCallId ?? "undefined",
                          Mdn: voiceTask.mdn ?? "undefined",
                      }
                    : {}),
                ...(chatTask
                    ? {
                          ChatId: chatTask.chatId ?? "undefined",
                          Mdn: chatTask.mdn?.value?.replace(/\D/g, "") ?? "undefined",
                          Queue: chatTask.routingQueue ?? "undefined",
                          SourceApp: chatTask.sourceApp ?? "undefined",
                          Partner: chatTask.partner ?? "undefined",
                      }
                    : {}),
                CommitHash: commitHash || "undefined",
                Timestamp: new Date().toISOString(),
            }),
        );
        toast.success("Copied debug info to clipboard!");
        setMenuOpen(false);
    };

    function handleProfileMenuView() {
        void dispatcher.dispatchAction("Click", "agent-profile-menu-view");
    }

    const logOff = async () => {
        await setNewAgentActivity(agentSdk.offlineAgentActivity);
        setMenuOpen(false);
    };

    const onSoundNotificationsClick = (isMuted: boolean) => {
        loggerWithContext.info("Sound notifications toggled");
        void dispatcher.dispatchAction("Click", "sound-notification-toggle", { isMuted });
    };

    const formattedVersionNumber = env.versionNumber.startsWith("v")
        ? `v${env.versionNumber.substring(3)}`
        : env.versionNumber;

    return (
        <Menu
            shadow="sm"
            radius="md"
            zIndex="var(--mantine-priority-highest)"
            opened={menuOpen}
            onChange={setMenuOpen}
            closeOnClickOutside
            closeOnItemClick={false}
        >
            <Menu.Target>
                <Button
                    color="var(--mantine-color-text)"
                    data-testid="agent-profile-menu"
                    id="agent-profile-menu"
                    leftSection={<UserInitial name={agentName} />}
                    data-menu-open={menuOpen}
                    className={classes.profileBtn}
                    onClick={handleProfileMenuView}
                    size="xs"
                    variant="subtle"
                >
                    <Text data-expert-name={agentName} size="xs">
                        {agentName}
                    </Text>
                    <Space w="xs" />
                    {menuOpen ? <CaretUp /> : <CaretDown />}
                </Button>
            </Menu.Target>
            <Menu.Dropdown>
                {isProactiveEnabled && <SoundNotificationsSwitch onClick={onSoundNotificationsClick} />}
                <Menu.Item
                    leftSection={<CopyIcon width={20} height={21} stroke="var(--mantine-color-dark-3)" />}
                    key="Copy Troubleshooting"
                    onClick={copyToDebug}
                >
                    <Text size="xs">Copy troubleshoot info</Text>
                </Menu.Item>
                <Menu.Item
                    leftSection={<Exit height={20} width={20} fill="var(--mantine-color-red-5)" />}
                    key="Log Off"
                    color="red.5"
                    onClick={() => void logOff()}
                    disabled={activity === agentSdk.offlineAgentActivity}
                >
                    <Text size="xs">End your shift</Text>
                </Menu.Item>

                {isLightModeEnabled && (
                    <>
                        <Menu.Divider />
                        <Stack id="settings-section" p="sm" w="100%" justify="center" align="center">
                            <ThemeControls />
                        </Stack>
                    </>
                )}

                <Menu.Divider />

                <Text size="xs" mb="sm" className={classes.versionText}>
                    {formattedVersionNumber}
                </Text>
            </Menu.Dropdown>
        </Menu>
    );
}
